// ============================================================
// pages/ProfilePage.jsx — User Profile Editor (Firebase)
// ============================================================

import React, { useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { UserService } from "../services/firebaseServices";

export default function ProfilePage({ showToast }) {
  const { user, profile, refreshProfile, signOut } = useAuth();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    displayName: profile?.displayName || user?.displayName || "",
    phone: profile?.phone || "",
    address: profile?.address || "",
    dateOfBirth: profile?.dateOfBirth?.toDate
      ? profile.dateOfBirth.toDate().toISOString().split("T")[0]
      : "",
    dietaryRestrictions: (profile?.preferences?.dietaryRestrictions || []).join(", "),
    emailNotifs: profile?.preferences?.communicationPrefs?.email ?? true,
    whatsappNotifs: profile?.preferences?.communicationPrefs?.whatsapp ?? true,
    pushNotifs: profile?.preferences?.communicationPrefs?.push ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const update = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await UserService.updateProfile(user.uid, {
        displayName: form.displayName,
        phone: form.phone,
        address: form.address,
        dateOfBirth: form.dateOfBirth ? new Date(form.dateOfBirth) : null,
        preferences: {
          dietaryRestrictions: form.dietaryRestrictions
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          favouriteProducts: profile?.preferences?.favouriteProducts || [],
          communicationPrefs: {
            email: form.emailNotifs,
            whatsapp: form.whatsappNotifs,
            push: form.pushNotifs,
          },
        },
      });
      await refreshProfile();
      showToast("Profile updated!", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to update profile", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await UserService.uploadProfilePicture(user.uid, file);
      await refreshProfile();
      showToast("Profile picture updated!", "success");
    } catch (err) {
      showToast("Failed to upload photo", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    showToast("Signed out. See you soon!", "success");
  };

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <div className="section-header">
        <div>
          <h2>My Profile</h2>
          <p>Manage your account details and preferences</p>
        </div>
      </div>

      {/* Profile Photo */}
      <div className="card" style={{ marginBottom: "1.5rem", textAlign: "center", padding: "2rem" }}>
        <div style={{ position: "relative", display: "inline-block" }}>
          {(profile?.photoURL || user?.photoURL) ? (
            <img src={profile?.photoURL || user?.photoURL} alt="Profile"
              style={{ width: 100, height: 100, borderRadius: "50%", objectFit: "cover", border: "3px solid var(--chai)" }} />
          ) : (
            <div style={{
              width: 100, height: 100, borderRadius: "50%", background: "var(--chai)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "2.5rem", color: "white", fontFamily: "'Playfair Display', serif",
            }}>
              {(profile?.displayName || user?.displayName || "?").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div style={{ marginTop: "1rem" }}>
          <button className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()}>
            {uploading ? "Uploading..." : "Change Photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={handlePhotoUpload} />
        </div>
        <div style={{ marginTop: "0.75rem", fontSize: "0.85rem", color: "var(--cocoa)" }}>
          {user?.email}
        </div>
        {profile?.loyaltyTier && (
          <div style={{ marginTop: "0.5rem" }}>
            <span className="inline-tag" style={{ textTransform: "capitalize", fontSize: "0.85rem" }}>
              {profile.loyaltyTier} Tier · {profile.loyaltyPoints || 0} pts
            </span>
          </div>
        )}
      </div>

      {/* Profile Form */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Personal Details</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="form-input" value={form.displayName}
              onChange={(e) => update("displayName", e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" type="tel" value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+230 5XXX XXXX" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Address</label>
          <input className="form-input" value={form.address}
            onChange={(e) => update("address", e.target.value)}
            placeholder="Your delivery address" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div className="form-group">
            <label className="form-label">Date of Birth</label>
            <input className="form-input" type="date" value={form.dateOfBirth}
              onChange={(e) => update("dateOfBirth", e.target.value)} />
            <div style={{ fontSize: "0.78rem", color: "var(--cocoa)", marginTop: 4 }}>
              We'll send you birthday bonus points! 🎂
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Dietary Restrictions</label>
            <input className="form-input" value={form.dietaryRestrictions}
              onChange={(e) => update("dietaryRestrictions", e.target.value)}
              placeholder="e.g. nut-free, vegan" />
            <div style={{ fontSize: "0.78rem", color: "var(--cocoa)", marginTop: 4 }}>
              Comma-separated
            </div>
          </div>
        </div>
      </div>

      {/* Communication Preferences */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Communication Preferences</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {[
            { key: "emailNotifs", label: "Email notifications", desc: "Order updates, promotions, newsletters" },
            { key: "whatsappNotifs", label: "WhatsApp messages", desc: "Booking confirmations, reminders" },
            { key: "pushNotifs", label: "Push notifications", desc: "Real-time order status updates" },
          ].map((pref) => (
            <label key={pref.key} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "0.75rem 1rem", borderRadius: 8, background: "var(--cream)", cursor: "pointer",
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{pref.label}</div>
                <div style={{ fontSize: "0.78rem", color: "var(--cocoa)" }}>{pref.desc}</div>
              </div>
              <input type="checkbox" checked={form[pref.key]}
                onChange={(e) => update(pref.key, e.target.checked)}
                style={{ width: 20, height: 20 }} />
            </label>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
        <button className="btn btn-danger" onClick={handleSignOut}>
          Sign Out
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
